import { logger } from "@/modules/logger";
import {
  AutoCleanupDecision,
  AutoCleanupInput,
  AutoCleanupResult,
  AutoCleanupNoteState,
} from "./auto-cleanup-policy.types";

/**
 * Determines if a note is in original state.
 * (We use this to determine if a note should be deleted permanently or moved to trash.)
 */
const isOriginalState = ({
  modelVersion,
  hasQueuedUpdates,
}: Pick<AutoCleanupNoteState, "modelVersion" | "hasQueuedUpdates">): boolean => {
  if (hasQueuedUpdates) return false;

  return modelVersion <= 1;
};

/**
 * Determines if a note is in original state.
 * (We use this to determine if a note should be deleted permanently or moved to trash.)
 */
const _shouldAutoCleanUp = (input: AutoCleanupInput): AutoCleanupDecision => {
  const { editor, note, runtime } = input;

  if (editor.isReadOnly) return { result: null };
  if (!editor.isNoteEmpty) return { result: null };

  if (note.isDeleted) return { result: null };
  if (note.isTrashed) return { result: null };
  if (!note.isOwnedByMe) return { result: null };
  if (!note.isContentLoaded) return { result: null };
  if (note.hasOpenTab) return { result: null };

  if (runtime.isAudioRecordingActive) return { result: null };
  if (runtime.hasRecording) return { result: null };
  if (runtime.hasSuggestion) return { result: null };
  if (runtime.hasSource) return { result: null };

  /**
   * If isAutoDeletable is false, ignore the note.
   */
  if (!runtime.isAutoDeletable) {
    return { result: null };
  }

  /**
   * Otherwise, follow normal logic:
   * - If it is in the original state (untouched) we can delete it permanently
   * - Otherwise, we trash it
   */
  if (
    isOriginalState({ modelVersion: note.modelVersion, hasQueuedUpdates: note.hasQueuedUpdates })
  ) {
    return { result: AutoCleanupResult.DeletePermanently };
  }

  return { result: AutoCleanupResult.MoveToTrash };
};

/**
 * Determines if a note should be automatically cleaned up.
 */
export const shouldAutoCleanUp = (input: AutoCleanupInput): AutoCleanupDecision => {
  const result = _shouldAutoCleanUp(input);

  logger.info({
    message: `[shouldAutoCleanUp] ${input.id}`,
    info: {
      id: input.id,
      result: result.result,
      editor: {
        isNoteEmpty: input.editor.isNoteEmpty,
        isReadOnly: input.editor.isReadOnly,
      },
      note: {
        hasOpenTab: input.note.hasOpenTab,
        hasQueuedUpdates: input.note.hasQueuedUpdates,
        isContentLoaded: input.note.isContentLoaded,
        isDeleted: input.note.isDeleted,
        isOwnedByMe: input.note.isOwnedByMe,
        isTrashed: input.note.isTrashed,
        modelVersion: input.note.modelVersion,
      },
      runtime: {
        hasRecording: input.runtime.hasRecording,
        hasSource: input.runtime.hasSource,
        hasSuggestion: input.runtime.hasSuggestion,
        isAudioRecordingActive: input.runtime.isAudioRecordingActive,
        isAutoDeletable: input.runtime.isAutoDeletable,
      },
    },
  });

  return result;
};
